import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import Products from 'merchandise-lib/constants/Product';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect } from 'react-router-dom';
import ProductInfoContainer from '../components/ProductInfoContainer';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import ValuePropsSection from '../components/common/ValuePropsSection';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import SkeletonLoadWrapper from '../components/common/loading/SkeletonLoadWrapper';
import HeaderLayout from '../components/layout/HeaderLayout';
import { ProductCardContainer } from '../components/merch-card/card-containers/ProductCardContainer';
import { MARKETING_PLUS_PAGE, MARKETING_PLUS_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { NavItemEnum } from '../constants/routing/NavItems';
import { ENTERPRISE } from '../constants/routing/Tabs';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import ErrorPage from '../pages/ErrorPage';
import { useAppSelector } from '../reducers';
import { isUngatedForMarketingPlusSelector } from '../selectors/GatingSelectors';
import { hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import { withErrorBoundary } from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import PhoneSupportMiddle from '../v2/components/phone-support/PhoneSupportMiddle';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { MARKETING_PLUS_CARD } from '../v2/constants/tracking/TrackingSources';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import { MarketingPlusFeatureTable } from '../v2/pages/marketing-plus/MarketingPlusFeatureTable';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MarketingPlusPage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('marketing-plus');
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const screen = SCREENS.MARKETING_PLUS;
  const navItem = NavItemEnum.MARKETING_PLUS;
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  useRedirectIfIneligible({
    condition: () => !isUngatedForMarketingPlus
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  if (hasPricingAndPurchasingFailed) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: PRICING_AND_PURCHASING_FAILED_STATUS
    });
  }
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: MARKETING_PLUS_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs("div", {
      "data-selenium-id": "page-success",
      children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
        name: "MARKETING_PLUS_PAGE"
      }), /*#__PURE__*/_jsx(HeaderLayout, {
        selectedNavItem: navItem,
        children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
          children: [/*#__PURE__*/_jsx(BannerSection, {
            screen: screen
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              nonProductIdentifier: navItem
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              nonProductIdentifier: navItem
            })]
          }), /*#__PURE__*/_jsx(ProductCardContainer, {
            screen: screen,
            productsToShow: ['marketing-plus-professional-bundle', 'marketing-plus-enterprise-bundle'],
            trackingSource: MARKETING_PLUS_CARD
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(CreditsCallout, {
              navItem: screen
            })
          }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !isAppReady,
            heights: [40],
            widths: ['100%'],
            children: /*#__PURE__*/_jsx(PageSection, {
              children: /*#__PURE__*/_jsx(MarketingPlusFeatureTable, {})
            })
          }), /*#__PURE__*/_jsx(PhoneSupportMiddle, {
            phoneSourceId: "marketingplus-middle",
            screen: screen,
            apiName: Products.MARKETING_PLUS_SUITE_BUNDLE_PROFESSIONAL
          }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab: ENTERPRISE,
              screen
            }
          }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: "marketingplus-footer",
            screen: screen,
            apiName: Products.MARKETING_PLUS_SUITE_BUNDLE_PROFESSIONAL
          })]
        })
      })]
    })
  });
};
export default withErrorBoundary(MarketingPlusPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: MARKETING_PLUS_PAGE
});